import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { updateProfile } from '@angular/fire/auth';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressBar } from '@angular/material/progress-bar';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../auth.service';
import { LanguageSelectorComponent } from '../../shared/components/language-selector/language-selector.component';
import { ToastService } from '../../shared/services/toast/toast.service';

@Component({
  selector: 'app-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatLabel,
    MatFormField,
    MatInput,
    MatIconButton,
    MatSuffix,
    MatIcon,
    MatButtonModule,
    MatProgressBar,
    TranslateModule,
    LanguageSelectorComponent,
    RouterLink,
  ],
})
export class SignupComponent implements OnInit {
  private fb = inject(FormBuilder);
  private authService = inject(AuthService);
  private router = inject(Router);
  private toast = inject(ToastService);
  private translate = inject(TranslateService);
  private aroute = inject(ActivatedRoute);

  loading = signal(false);
  showEmailForm = signal(true);
  hidePassword = signal(true);
  hideConfirmPassword = signal(true);
  errorMessage = signal<string | null>(null);
  inviteInfo = signal<any>(null);

  showSignupPoster = environment.showSignupPoster ?? false;

  signupForm = this.fb.group({
    fullName: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(8)]],
    confirmPassword: [''],
    website: [''], // Honeypot field (hidden in UI)
  });

  get passwordMismatch(): boolean {
    const pw = this.signupForm.get('password')?.value;
    const cpw = this.signupForm.get('confirmPassword')?.value;
    return !!cpw && pw !== cpw;
  }

  get isFormValid(): boolean {
    const f = this.signupForm;
    return !f.invalid && !this.passwordMismatch && !!f.get('confirmPassword')?.value && !!f.get('fullName')?.value?.trim();
  }

  ngOnInit() {
    const lang = this.translate.currentLang || localStorage.getItem('language') || 'fr';
    this.translate.use(lang);

    const email = this.aroute.snapshot.queryParamMap.get('email');
    if (email) {
      this.signupForm.patchValue({ email });
      // We can also default to showing the email form if it's not a google sign in
      this.showEmailForm.set(true);
    }

    const inviteToken = this.aroute.snapshot.queryParamMap.get('invite');
    if (inviteToken) {
      this.authService.getInviteInfo(inviteToken).subscribe({
        next: (res) => {
          if (res.payload) {
            this.inviteInfo.set(res.payload);
          }
        },
        error: () => {
          this.toast.snackbar.open(this.translate.instant('Snackbar.Error') || 'Invalid or expired invitation', 'Ok', { duration: 3000 });
        },
      });
    }
  }

  togglePasswordVisibility() {
    this.hidePassword.update((v) => !v);
  }

  toggleConfirmPasswordVisibility() {
    this.hideConfirmPassword.update((v) => !v);
  }

  toggleEmailForm() {
    this.showEmailForm.update((v) => !v);
  }

  // ── Provider signup (Google / Microsoft) ─────────────────────
  async signupWithProvider(provider: 'google' | 'microsoft') {
    this.loading.set(true);
    this.errorMessage.set(null);

    try {
      // 1. Sign in with provider (creates Firebase Auth if new)
      await this.authService
        .loginWithProvider(provider)
        .then(async () => {
          // loginWithProvider already calls getUserData which may fail
          // for a brand new user (no backend record yet). That's OK.
        })
        .catch(async (err) => {
          // If getUserData fails because user doesn't exist in backend yet,
          // that's expected for a new signup. We proceed to workspace creation.
          // But if it succeeded, it means the user already has an account — just redirect.
          if (err?.status === 403 || err?.status === 401) {
            throw err; // Re-throw actual auth errors
          }
        });

      // 2. Get the current Firebase user's info
      const firebaseUser = this.authService['auth'].currentUser;
      if (!firebaseUser) {
        throw new Error('No Firebase user after provider sign-in');
      }

      const email = firebaseUser.email;
      const fullName = firebaseUser.displayName || email.split('@')[0];
      const returnUrl = this.aroute.snapshot.queryParamMap.get('returnUrl') || '/knowledge';

      if (returnUrl.includes('autoJoin=true')) {
        this.router.navigateByUrl(returnUrl);
        return;
      }

      // 3. Call backend to provision free workspace or get checkout URL
      const result = await firstValueFrom(this.authService.createFreeWorkspace({ email, fullName }));

      if (result.url) {
        window.location.href = result.url;
        return;
      }

      if (result.success) {
        if (result.tenantId) {
          localStorage.setItem('active_workspace', result.tenantId);
        }
        // 4. Fetch user data (Firebase session is already active)
        await this.authService.getUserData({ id: firebaseUser.uid });
        if (!localStorage.getItem('sessionUUID')) {
          await this.authService.saveUserSession(firebaseUser.uid);
          this.authService.startSessionSubscription(firebaseUser.uid);
        }

        // Accept invitation if one is present
        const inviteToken = this.aroute.snapshot.queryParamMap.get('invite');
        if (inviteToken) {
          try {
            await firstValueFrom(this.authService.acceptInvite(inviteToken));
          } catch (err) {
            console.error('Failed to accept invite', err);
          }
        }

        const returnUrl = this.aroute.snapshot.queryParamMap.get('returnUrl') || '/knowledge';
        this.router.navigateByUrl(returnUrl);
      }
    } catch (err: any) {
      console.error('Provider signup error:', err);
      // Don't show error if user already navigated (loginWithProvider succeeded fully)
      if (this.router.url === '/signup') {
        this.errorMessage.set(this.translate.instant('Sessions.Signup.ErrorGeneric'));
      }
    } finally {
      this.loading.set(false);
    }
  }

  // ── Email + password signup ──────────────────────────────────
  async signupWithEmail() {
    if (!this.isFormValid) return;

    const { fullName, email, password, website } = this.signupForm.getRawValue();

    if (website) {
      console.warn('Bot detection triggered via honeypot.');
      // Silently exit to waste bot resources without hitting Firebase or Backend APIs
      this.loading.set(true);
      setTimeout(() => {
        this.loading.set(false);
      }, 1000);
      return;
    }

    this.loading.set(true);
    this.errorMessage.set(null);

    try {
      // 1. Create Firebase Auth user
      const cred = await this.authService.signupWithEmailAndPassword(email, password);
      if (fullName && cred.user) {
        await updateProfile(cred.user, { displayName: fullName });
      }

      // Send email verification
      await this.authService.sendVerificationEmail();

      const returnUrl = this.aroute.snapshot.queryParamMap.get('returnUrl') || '/knowledge';
      if (returnUrl.includes('autoJoin=true')) {
        this.router.navigateByUrl(returnUrl);
        return;
      }

      // 2. Provision free workspace
      const result = await firstValueFrom(this.authService.createFreeWorkspace({ email, fullName }));

      if (result.url) {
        window.location.href = result.url;
        return;
      }

      if (result.success) {
        if (result.tenantId) {
          localStorage.setItem('active_workspace', result.tenantId);
        }
        // 3. Fetch user data (Firebase session is already active)
        if (cred.user) {
          await this.authService.getUserData({ id: cred.user.uid });
          if (!localStorage.getItem('sessionUUID')) {
            await this.authService.saveUserSession(cred.user.uid);
            this.authService.startSessionSubscription(cred.user.uid);
          }
        }

        // Accept invitation if one is present
        const inviteToken = this.aroute.snapshot.queryParamMap.get('invite');
        if (inviteToken) {
          try {
            await firstValueFrom(this.authService.acceptInvite(inviteToken));
          } catch (err) {
            console.error('Failed to accept invite', err);
          }
        }

        const returnUrl = this.aroute.snapshot.queryParamMap.get('returnUrl') || '/knowledge';
        this.router.navigateByUrl(returnUrl);
      }
    } catch (err: any) {
      console.error('Email signup error:', err);
      const code = err?.code || '';
      if (code === 'auth/email-already-in-use') {
        this.errorMessage.set(this.translate.instant('Sessions.Signin.EmailAlreadyInUseError'));
      } else if (code === 'auth/weak-password' || code === 'auth/password-does-not-meet-requirements') {
        this.errorMessage.set(this.translate.instant('Sessions.Signin.WeakPasswordError'));
      } else {
        this.errorMessage.set(this.translate.instant('Sessions.Signup.ErrorGeneric'));
      }
    } finally {
      this.loading.set(false);
    }
  }
}
